import { memo, useEffect, useState } from 'react';
import Big from 'big.js';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import useMiyoFee from '@hooks/useMiyoFee';
import ProductPricing from './ProductPricing';
import MobileHeaderPricing from './MobileHeaderPricing';

type Props = {
  variant?: 'default' | 'mobile-header';
  isBundle?: boolean;
} & any;

const ProductPricingWrapper = ({ variant = 'default', isBundle = false, ...restProps }: Props) => {
  const {
    item,
    addOns,
    isCustomShop = false,
    customizationFees = { total: 0, perItem: 0 },
    customRate = null,
  } = restProps;

  const rates = useRates({ item, addOns, isCustomShop, customizationFees, customRate });

  if (variant === 'mobile-header') return <MobileHeaderPricing {...restProps} rates={rates} />;

  return <ProductPricing isBundle={isBundle} {...restProps} rates={rates} />;
};

export default memo(ProductPricingWrapper);

const calculateRate = ({
  item,
  rateField,
  fallbackRateField = null,
  addOns = [],
  rateAddition = 0,
  upsellAddition = 0,
}) => {
  return item[rateField] == null
    ? null
    : Big(item[rateField])
        .plus(
          addOns
            .map((i) => Big(i[rateField] ?? i[fallbackRateField] ?? 0))
            .reduce((acc, val) => acc.plus(val), Big(0))
        )
        .plus(rateAddition)
        .plus(upsellAddition)
        .toNumber();
};

export const useRates = ({ item, addOns, isCustomShop, customizationFees, customRate }) => {
  const { upsell } = useSelector((state: any) => state.pdpItem);
  const selectedUpsells = upsell?.selected || [];

  const fee = useMiyoFee();

  const rateAddition = fee || (isCustomShop ? customizationFees.total : 0);
  const upsellRates = useMemo(() => {
    let vals = {
      ourPrice: 0,
      competitorsPrice: 0,
      rate: 0,
    };

    if (!!selectedUpsells && selectedUpsells?.length > 0) {
      vals = selectedUpsells.reduce((acc, val) => {
        return {
          ourPrice: acc.ourPrice + (val?.itemRate ?? 0),
          competitorsPrice: acc.competitorsPrice + (val?.baseRate ?? 0),
          rate: acc.rate + (val?.rate ?? 0),
        };
      }, vals);
    }

    return vals;
  }, [upsell]);

  const [rates, setRates] = useState({
    competitorPrice: item.competitorPrice,
    ourPrice: item.ourPrice,
    salePrice: item.salePrice,
    rate: customRate?.rate ?? item.salePrice ?? item.ourPrice,
    youSavePercent: item.youSavePercent,
  });

  const assignRates = () => {
    const competitorPrice = calculateRate({
      item,
      addOns,
      rateAddition,
      rateField: 'competitorPrice',
      upsellAddition: upsellRates.competitorsPrice,
    });
    const competitorPriceForDiv = competitorPrice === 0 ? Big(1) : Big(competitorPrice);
    const salePrice = calculateRate({
      item,
      addOns,
      rateAddition,
      fallbackRateField: 'ourPrice',
      rateField: 'salePrice',
      upsellAddition: upsellRates.rate,
    });
    const ourPrice = calculateRate({
      item,
      addOns,
      rateAddition,
      rateField: 'ourPrice',
      upsellAddition: !!salePrice ? upsellRates.ourPrice : upsellRates.rate,
    });
    const rate = customRate?.rate ?? salePrice ?? ourPrice;
    const youSavePercent = Big(competitorPrice).minus(rate).div(competitorPriceForDiv).toNumber();

    setRates({
      competitorPrice,
      ourPrice,
      salePrice,
      rate,
      youSavePercent,
    });
  };

  useEffect(() => {
    assignRates();
  }, [item, addOns, upsellRates, customRate]);

  return rates;
};
